/* ================================================================
 * Tour stop · App lifecycle, the Three.js stage, and the
 *            fixed-timestep accumulator that decouples sim from
 *            display rate
 * ----------------------------------------------------------------
 * See docs/code-roadmap.md §2 (frame trace) and §6 (the client)
 * for the surrounding narrative. This is the spine of the entire
 * client; everything else is a sub-system this class composes.
 *
 * Three concepts to learn here:
 *
 * 1) The render stack.
 *    `Stage.create()` hard-requires WebGPU, builds the renderer +
 *    bloom post-pipeline, and appends its <canvas> under #app. The
 *    world is a Three.js scene composed of views:
 *
 *      stage.scene
 *        ├── RinkView      (ice + markings shader, glass boards,
 *        │                  neon trim, goal frames, starfield)
 *        ├── SkatersView   (every skater surface, instanced)
 *        ├── GoaliesView   (solid domes + shield + save ring)
 *        └── PuckView3D    (hero puck + the scene's only light)
 *
 *    The HUD (scoreboard, faceoff bar, offscreen arrow) is a DOM
 *    overlay (`hudRoot`) above the canvas, not part of the scene —
 *    crisp text, no glyph atlas, renderer-independent. Camera: the
 *    sim-space follow logic lives in GameCamera (unchanged from the
 *    2D era); CameraRig projects its smoothed center into an actual
 *    perspective pose, with an overview framing behind the menu.
 *    Every visual tunable lives in render/style/*.style.json — see
 *    the material lab (`?lab=`) for the live-edit loop.
 *
 * 2) The fixed-timestep accumulator (look at `onFrame` + `stepSim`).
 *    The renderer invokes our callback via requestAnimationFrame —
 *    60 Hz on a normal monitor, 120 Hz on ProMotion, 20 Hz during a
 *    frame spike. We DO NOT advance the sim once per frame:
 *
 *      const delta = loop.nextDeltaMs(now);   // capped at 250 ms
 *      loop.advance(delta, stepSim);          // 0..N fixed steps
 *
 *    The accumulator mechanics live in @neoweb/core's `FixedStepLoop`
 *    (the standard "Fix Your Timestep" pattern, Glenn Fiedler); this
 *    class keeps the policy (when to tick vs. idle, what a tick does).
 *    The sim runs at exactly 60 Hz regardless of how fast the renderer
 *    runs; the 250 ms delta cap prevents the spiral-of-death after a
 *    long pause (browser GC, tab hidden). A more elaborate
 *    implementation would interpolate between the last two ticks for
 *    sub-frame smoothness; at 60 Hz we don't need that yet.
 *
 * 3) Web Audio unlock-on-gesture.
 *    Browsers refuse to play audio until the user has gestured at
 *    the page (keydown, pointerdown, etc.). We register a one-shot
 *    listener that calls `audio.unlock()` on the first such event,
 *    then unregisters itself. Until that happens, the AudioEngine
 *    happily registers events and loads buffers, but no sound comes
 *    out. This is the universal pattern for any Web Audio app.
 *
 * The renderer reads `MatchSnapshot` (a structuredClone of state),
 * NOT the live MatchState. That's the snapshot-driven rendering
 * pattern and the foundation of the future client/server split.
 *
 * Looking up the P1-controlled player goes through `snap.control` —
 * the snapshot carries each team's `controlledByHuman` map so the
 * renderer can resolve "which player does the camera follow" without
 * hardcoding array indices.
 * ================================================================ */

import { tickBots } from "@arcade-hockey/bots";
import type { PlayerId, PlayerInput } from "@arcade-hockey/shared";
import {
  type HumanSeatsConfig,
  type MatchSnapshot,
  type MatchState,
  ROLE_PROGRESSION,
  type RosterSizes,
  TACTIC_IDS,
  type TacticId,
  type Vec2,
  createMatch,
  drainEvents,
  takeSnapshot,
  tickMatch,
  tuning,
} from "@arcade-hockey/sim";
import { AudioEngine } from "@neoweb/audio";
import { FixedStepLoop, onFirstGesture } from "@neoweb/core";
import { Stage } from "@neoweb/three";
import { stereoPanFromWorld } from "../audio/Spatializer2D.js";
import { busTopology } from "../audio/bus-topology.js";
import { type AudioEventId, eventDefs } from "../audio/event-defs.js";
import { FaceoffBar } from "../hud/FaceoffBar.js";
import { OffscreenArrow } from "../hud/OffscreenArrow.js";
import { Scoreboard } from "../hud/Scoreboard.js";
import { MainMenu, type MainMenuConfig, type MatchMode } from "../menu/MainMenu.js";
import { ResultsPanel } from "../menu/ResultsPanel.js";
import { CameraRig } from "../render/CameraRig.js";
import { GameCamera } from "../render/GameCamera.js";
import { bloomStyle } from "../render/style/style-schema.js";
import { FxDirector } from "../render/views/FxDirector.js";
import { GoaliesView } from "../render/views/GoaliesView.js";
import { PuckView3D } from "../render/views/PuckView3D.js";
import { RinkView } from "../render/views/RinkView.js";
import { SkatersView, setActiveSkatersView } from "../render/views/SkatersView.js";
import { ReplayRecorder } from "../replay/ReplayRecorder.js";
import { EventDispatcher } from "./EventDispatcher.js";
import { InputController } from "./InputController.js";

export type AppOptions = {
  parent: HTMLElement;
  seed?: number;
};

// The high-level UI mode. `Menu` blocks sim ticking and shows the main menu
// overlay; `Playing` runs the fixed-timestep accumulator over the live match;
// `Results` freezes the final frame and shows the post-match panel.
export type Screen = "Menu" | "Playing" | "Results";

// Resolve a MainMenu mode choice to the sim's HumanSeatsConfig. For
// `cpuvcpu`, both seats on both teams are bot-controlled, so tickBots
// produces an input for every skater. For `pvcpu`, fall back to today's
// default by leaving humanSeats undefined.
function humanSeatsForMode(mode: MatchMode): HumanSeatsConfig | undefined {
  if (mode === "cpuvcpu") {
    return { home: { P1: null, P2: null }, away: { P1: null, P2: null } };
  }
  return undefined;
}

// Phase G slice 1 dev flag: `?debug=roles` adds role labels (LW/C/...) to
// each skater. Comma-separated values are supported so we can layer further
// debug overlays in later slices (e.g. `?debug=roles,situation`).
function debugFlagsFromUrl(): Set<string> {
  if (typeof window === "undefined") return new Set();
  const raw = new URLSearchParams(window.location.search).get("debug");
  if (raw === null) return new Set();
  return new Set(
    raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
  );
}

export class App {
  private state: MatchState;
  private stage: Stage | null = null;
  private gameCamera: GameCamera;
  private cameraRig: CameraRig | null = null;
  private rinkView: RinkView | null = null;
  private skatersView: SkatersView | null = null;
  private goaliesView: GoaliesView | null = null;
  private puckView: PuckView3D | null = null;
  private fxDirector: FxDirector | null = null;
  // DOM overlay root for the HUD (scoreboard, faceoff bar, offscreen arrow).
  // Sits above the canvas, below the menus; pointer-events pass through.
  private readonly hudRoot = document.createElement("div");
  private scoreboard: Scoreboard | null = null;
  private faceoffBar: FaceoffBar | null = null;
  private offscreenArrow: OffscreenArrow | null = null;
  private inputController = new InputController();
  private audio = new AudioEngine<AudioEventId>(busTopology);
  // Removes the first-gesture audio-unlock listeners; set in start(), called in destroy() if the
  // gesture never happened.
  private removeGestureListener: (() => void) | null = null;
  dispatcher: EventDispatcher | null = null;
  // Fixed-timestep host: owns the accumulator + frame-delta cap. The sim runs at exactly 60 Hz
  // regardless of display rate. See @neoweb/core.
  private readonly loop = new FixedStepLoop();
  private destroyed = false;
  // Optional hook fired once per sim tick — fed (tickBeingApplied, mergedInputs).
  // The dev-only ReplayController uses this to record into a Replay; in
  // production builds the dynamic-import block is tree-shaken out and the hook
  // stays null, costing a single boolean check per tick.
  onTickComplete: ((tick: number, mergedInputs: ReadonlyArray<PlayerInput>) => void) | null = null;
  // Optional override of the per-frame loop body. When set (by the dev-only
  // ReplayController during playback), the default fixed-timestep accumulator
  // is bypassed entirely and `playbackDriver` is responsible for advancing
  // the sim and calling onTickComplete itself.
  playbackDriver: ((deltaMs: number) => void) | null = null;
  // Fires after restartMatch builds the new state. The ReplayController hooks
  // this to clear its in-flight recording and start a fresh one for the new
  // seed.
  onMatchRestart: ((seed: number) => void) | null = null;
  private currentSeed: number;
  private screen: Screen = "Menu";
  private menu: MainMenu | null = null;
  private resultsPanel: ResultsPanel | null = null;
  private replayRecorder: ReplayRecorder;
  // The most-recent startMatch config — used to decide on auto-download and
  // filename prefix when the match ends, and for "Play again".
  private currentMode: MatchMode = "pvcpu";
  // Last-used roster sizes; restartMatch reuses this so F9 Capture/Play
  // round-trips preserve whatever the user picked on the menu.
  private currentRosterSizes: RosterSizes = {
    home: ROLE_PROGRESSION.length,
    away: ROLE_PROGRESSION.length,
  };
  private autoCaptureReplay = false;
  // Hot-path scratch — mutated once per frame, never reassigned. Keeps the
  // render loop allocation-free; see .claude/skills/writing-zero-alloc-inner-loop/SKILL.md.
  private readonly screenScratch = { width: 0, height: 0 };
  // Stable listener function — captured once, reused for every spatial audio
  // event. Reads the live stage camera + this.screenScratch at fire time.
  private readonly panListener = (pos: Vec2): number =>
    this.stage === null ? 0 : stereoPanFromWorld(pos, this.stage.camera, this.screenScratch);
  // Persistent merged-inputs buffer fed to tickMatch; refilled each tick.
  private readonly tickInputsScratch: PlayerInput[] = [];
  // Frozen at construction; kept for future debug overlays.
  private readonly debugFlags: ReadonlySet<string> = debugFlagsFromUrl();

  constructor(private opts: AppOptions) {
    this.currentSeed = opts.seed ?? 0xa17cade;
    this.state = createMatch({ seed: this.currentSeed });
    this.replayRecorder = new ReplayRecorder(this.currentSeed);
    this.gameCamera = new GameCamera();
    void this.debugFlags;
  }

  async start(): Promise<void> {
    this.stage = await Stage.create(this.opts.parent, { bloom: bloomStyle });
    this.cameraRig = new CameraRig(this.stage.camera);

    this.rinkView = new RinkView();
    this.stage.scene.add(this.rinkView.root);
    this.goaliesView = new GoaliesView();
    this.stage.scene.add(this.goaliesView.root);
    this.puckView = new PuckView3D();
    this.stage.scene.add(this.puckView.root);
    this.buildSkatersView();
    this.fxDirector = new FxDirector(this.rinkView, this.puckView, this.cameraRig);

    this.opts.parent.style.position = "relative";
    Object.assign(this.hudRoot.style, {
      position: "absolute",
      inset: "0",
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: "100",
      display: "none",
    });
    this.opts.parent.appendChild(this.hudRoot);
    this.inputController.attach();

    // Phase G slice 6: hidden tactic-switch hotkeys for CPU-vs-CPU
    // iteration. 1/2/3 cycle HOME's tactic; Q/W/E cycle AWAY's. Gated on
    // import.meta.env.DEV so production never accidentally swaps tactics.
    // No on-screen UI for now — open devtools to read snapshot().control
    // if you need to confirm the change.
    if (import.meta.env.DEV) {
      window.addEventListener("keydown", this.handleTacticHotkey);
    }

    // Browsers refuse to play audio until the user gestures at the page. Unlock once, on the
    // first keydown/pointerdown; onFirstGesture removes its own listeners after firing.
    this.removeGestureListener = onFirstGesture(window, () => {
      void (async () => {
        await this.audio.unlock();
        // Stable listener bound once; AudioEngine.post invokes it directly.
        this.audio.setListener(this.panListener);
      })();
    });

    this.dispatcher = new EventDispatcher(this.audio, this.fxDirector);
    void (async () => {
      // Wait for unlock to set up the context. If never unlocked, no audio plays.
      // We optimistically register events; they'll load buffers but stay silent until unlock.
      for (const [id, def] of Object.entries(eventDefs)) {
        if (def.url === "") continue;
        await this.audio.registerEvent(id as AudioEventId, def);
      }
    })();

    this.scoreboard = new Scoreboard();
    this.hudRoot.appendChild(this.scoreboard.root);
    this.faceoffBar = new FaceoffBar();
    this.hudRoot.appendChild(this.faceoffBar.root);

    this.offscreenArrow = new OffscreenArrow();
    this.hudRoot.appendChild(this.offscreenArrow.root);

    // Dev-only debug bundle (replay overlay + recorder). Awaited here so the
    // recorder is hooked up BEFORE the ticker fires its first tick — otherwise
    // the recording would miss the opening ticks of the match. Production
    // builds with import.meta.env.DEV === false tree-shake the entire branch.
    if (import.meta.env.DEV) {
      const { attachDebug } = await import("../debug/index.js");
      if (!this.destroyed) attachDebug(this);
    }

    // Mount the main menu. It stays up until the user clicks Start, at which
    // point we restart the match with the chosen seed + mode and flip screen
    // to "Playing".
    this.menu = new MainMenu((config) => this.onMenuStart(config));
    this.menu.attach(this.opts.parent);

    // Pre-mount the results panel (hidden until MatchEnded).
    this.resultsPanel = new ResultsPanel({
      onBackToMenu: () => this.backToMenu(),
      onPlayAgain: () => this.playAgain(),
    });
    this.resultsPanel.attach(this.opts.parent);

    // `__app` is exposed before this async boot finishes; a caller (smoke
    // harness, devtools) may already have called startMatch. Honor the
    // screen it set instead of overruling it with menu-up defaults.
    if (this.screen !== "Menu") {
      this.menu.hide();
      this.cameraRig.setMode("follow");
    }

    this.stage.renderer.setAnimationLoop(this.onFrame);
  }

  // (Re)build the instanced skater view from the current match state. Called
  // at boot and on every match (re)start so roster-size changes are honored.
  private buildSkatersView(): void {
    if (this.stage === null) return;
    if (this.skatersView !== null) this.stage.scene.remove(this.skatersView.root);
    this.skatersView = new SkatersView(this.snapshot());
    setActiveSkatersView(this.skatersView);
    this.stage.scene.add(this.skatersView.root);
  }

  private backToMenu(): void {
    if (this.resultsPanel !== null) this.resultsPanel.hide();
    if (this.menu !== null) this.menu.show();
    this.hudRoot.style.display = "none";
    this.screen = "Menu";
    this.cameraRig?.setMode("overview");
  }

  private playAgain(): void {
    if (this.resultsPanel !== null) this.resultsPanel.hide();
    this.startMatch({
      seed: this.currentSeed,
      mode: this.currentMode,
      autoCaptureReplay: this.autoCaptureReplay,
      rosterSizes: this.currentRosterSizes,
    });
  }

  private handleMatchEnded(winner: "home" | "away" | "Draw"): void {
    if (this.screen !== "Playing") return; // Idempotent — only act once.
    this.screen = "Results";
    if (this.resultsPanel === null) return;
    const description = `${this.currentMode} seed=${this.currentSeed}`;
    const replay = this.replayRecorder.capture(this.state, description);
    this.resultsPanel.show({
      homeScore: this.state.score.home,
      awayScore: this.state.score.away,
      winner,
      replay,
      filenamePrefix: this.currentMode === "cpuvcpu" ? "cpu-vs-cpu" : "match",
      autoDownloadOnShow: this.autoCaptureReplay,
    });
  }

  private onMenuStart(config: MainMenuConfig): void {
    this.startMatch(config);
  }

  // Public match-start API. Resets sim state with the picked seed + mode,
  // hides the menu, flips screen to "Playing", and fires onMatchRestart so
  // the replay recorder begins fresh from tick 0.
  startMatch(config: MainMenuConfig): void {
    this.currentSeed = config.seed;
    this.currentMode = config.mode;
    this.currentRosterSizes = config.rosterSizes;
    this.autoCaptureReplay = config.autoCaptureReplay;
    this.state = createMatch({
      seed: config.seed,
      humanSeats: humanSeatsForMode(config.mode),
      rosterSizes: config.rosterSizes,
    });
    this.replayRecorder.reset(config.seed, config.rosterSizes);
    this.loop.reset();
    this.screen = "Playing";
    this.hudRoot.style.display = "block";
    this.buildSkatersView();
    this.cameraRig?.setMode("follow");
    if (this.menu !== null) this.menu.hide();
    if (this.resultsPanel !== null) this.resultsPanel.hide();
    if (this.onMatchRestart !== null) this.onMatchRestart(config.seed);
  }

  private readonly handleTacticHotkey = (ev: KeyboardEvent): void => {
    // 1/2/3 = HOME tactic balanced/trap/aggressive
    // Q/W/E = AWAY tactic balanced/trap/aggressive
    const map: Record<string, { team: "home" | "away"; idx: number }> = {
      Digit1: { team: "home", idx: 0 },
      Digit2: { team: "home", idx: 1 },
      Digit3: { team: "home", idx: 2 },
      KeyQ: { team: "away", idx: 0 },
      KeyW: { team: "away", idx: 1 },
      KeyE: { team: "away", idx: 2 },
    };
    const entry = map[ev.code];
    if (entry === undefined) return;
    const tactic = TACTIC_IDS[entry.idx];
    if (tactic === undefined) return;
    this.setTeamTactic(entry.team, tactic);
    // eslint-disable-next-line no-console
    console.log(`[tactic] ${entry.team} → ${tactic}`);
  };

  setTeamTactic(teamId: "home" | "away", tactic: TacticId): void {
    const team = this.state.control.find((c) => c.teamId === teamId);
    if (team === undefined) return;
    team.tactic = tactic;
  }

  // Replace the running match with a fresh one for the given seed. Used by
  // the replay overlay's "Play this replay" button. `rosterSizes` is a
  // one-shot override — the replay overlay passes `replay.rosterSizes` when
  // starting playback so the rebuilt match matches the recording. The menu's
  // last-chosen sizes (this.currentRosterSizes) are NOT mutated, so stopping
  // playback returns to live at the menu's selection. Also flips screen to
  // "Playing" (and hides the menu if it was showing) so a dev pressing
  // F9→Play while still on the menu sees the replay run.
  restartMatch(seed: number, rosterSizes?: RosterSizes): MatchState {
    this.currentSeed = seed;
    const effectiveSizes = rosterSizes ?? this.currentRosterSizes;
    this.state = createMatch({
      seed,
      humanSeats: humanSeatsForMode(this.currentMode),
      rosterSizes: effectiveSizes,
    });
    this.replayRecorder.reset(seed, effectiveSizes);
    this.loop.reset();
    this.screen = "Playing";
    this.hudRoot.style.display = "block";
    this.buildSkatersView();
    this.cameraRig?.setMode("follow");
    if (this.menu !== null) this.menu.hide();
    if (this.resultsPanel !== null) this.resultsPanel.hide();
    if (this.onMatchRestart !== null) this.onMatchRestart(seed);
    return this.state;
  }

  // Production-eligible accessor used by the F9 dev overlay's "Capture
  // current game" button. The recorder is always live whenever a match is
  // running, so this just returns it.
  getRecorder(): ReplayRecorder {
    return this.replayRecorder;
  }

  // Live state accessor. Exposed for the dev-only ReplayController (which
  // reads state.tick/hash to populate the recorder) and for the visual smoke
  // harness. Production code should consume `snapshot()` instead.
  getState(): MatchState {
    return this.state;
  }

  getSeed(): number {
    return this.currentSeed;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    window.removeEventListener("keydown", this.handleTacticHotkey);
    this.removeGestureListener?.();
    this.stage?.renderer.setAnimationLoop(null);
    this.inputController.detach();
    if (this.menu !== null) {
      this.menu.detach();
      this.menu = null;
    }
    if (this.resultsPanel !== null) {
      this.resultsPanel.detach();
      this.resultsPanel = null;
    }
    this.scoreboard?.destroy();
    this.faceoffBar?.destroy();
    this.offscreenArrow?.destroy();
    this.hudRoot.remove();
    this.stage?.destroy();
  }

  snapshot() {
    return takeSnapshot(this.state);
  }

  // Test-only hook. Forces a player into the Stunned action for `ticks`
  // ticks, bypassing the body-check pipeline so the visual smoke and the
  // throwaway iteration spec don't have to engineer a head-on sprint
  // collision from scripted inputs. Not used by any non-test code path.
  debugForceStun(playerId: string, ticks: number): void {
    const p = this.state.playersById.get(playerId as PlayerId);
    if (!p) return;
    p.action = "Stunned";
    p.stunTicks = ticks;
    p.actionTimer = 0;
  }

  // Test-only hook. Toggles the pulled flag on one team's goalie so the
  // scratch iteration spec and the visual smoke can sample the dim-state
  // rendering without driving the goalie-pull toggle through the input layer.
  debugForceGoaliePull(teamId: "home" | "away"): void {
    const g = this.state.goalies.find((x) => x.teamId === teamId);
    if (!g) return;
    g.pulled = !g.pulled;
  }

  // Test-only hook. Skip the active Faceoff window so smoke tests that
  // exercise Play mechanics don't have to drive the new target-pick /
  // Pass-press faceoff commit sequence. Flips phase to Play with the
  // standard period clock.
  debugSkipFaceoff(): void {
    this.state.clock.phase = "Play";
    this.state.clock.remainingTicks = tuning.clock.periodTicks;
    this.state.clock.nextPlayPhase = undefined;
    this.state.rules.faceoffIntents = undefined;
  }

  // One fixed sim step: merge this tick's inputs, advance the sim, feed the recorder + dev hook.
  // Bound once (stable reference) so the per-frame loop.advance call allocates nothing.
  private readonly stepSim = (): void => {
    const humanInputs = this.inputController.pollInputs(this.state.tick);
    const botInputs = tickBots(this.state, this.state.tick);
    this.tickInputsScratch.length = 0;
    for (const input of humanInputs) this.tickInputsScratch.push(input);
    for (const input of botInputs) this.tickInputsScratch.push(input);
    const tickBeingApplied = this.state.tick;
    tickMatch(this.state, this.tickInputsScratch);
    this.replayRecorder.onTick(tickBeingApplied, this.tickInputsScratch);
    if (this.onTickComplete !== null) {
      this.onTickComplete(tickBeingApplied, this.tickInputsScratch);
    }
  };

  private readonly onFrame = (now: number): void => {
    // While the menu is up the sim must not advance — no tick, no event
    // drain — but the renderer still draws (the menu floats over the live
    // overview framing). The playback path is exempt because the dev replay
    // overlay drives its own tick loop and can be invoked from the menu
    // (F9 → paste → Play).
    if (this.screen !== "Playing" && this.playbackDriver === null) {
      // Keep the frame clock current so the delta doesn't spike to the cap the
      // first frame after Start is clicked.
      this.loop.markFrame(now);
      this.renderFrame(this.snapshot(), null, now, 1 / 60);
      return;
    }

    const delta = this.loop.nextDeltaMs(now);

    if (this.playbackDriver !== null) {
      // ReplayController owns the tick loop in playback mode. It calls
      // tickMatch (and onTickComplete) at whatever cadence its speed/pause
      // state demands.
      this.playbackDriver(delta);
    } else {
      this.loop.advance(delta, this.stepSim);
    }

    // Scan this frame's accumulated events for MatchEnded. Fires at most
    // once per match — handleMatchEnded is idempotent past the first call.
    if (this.screen === "Playing") {
      for (const ev of this.state.events) {
        if (ev.type === "MatchEnded") {
          this.handleMatchEnded(ev.winner);
          break;
        }
      }
    }

    // One snapshot per frame; passed into every consumer below.
    const snap = this.snapshot();
    const homeControlId =
      snap.control.find((t) => t.teamId === "home")?.controlledByHuman.P1 ?? null;
    const dtSec = Math.min(0.05, delta / 1000);
    this.gameCamera.update(snap, homeControlId, dtSec);

    this.renderFrame(snap, homeControlId, performance.now(), dtSec);
    drainEvents(this.state);
  };

  // Per-frame render pass: camera rig pose, world views, FX decay, DOM HUD,
  // and the post-pipeline render. Also runs while the menu is up (with a
  // static snapshot) so the overview framing stays alive behind the menu.
  private renderFrame(
    snap: MatchSnapshot,
    homeControlId: PlayerId | null,
    nowMs: number,
    dtSec: number,
  ): void {
    if (this.stage === null || this.cameraRig === null) return;
    this.screenScratch.width = this.opts.parent.clientWidth;
    this.screenScratch.height = this.opts.parent.clientHeight;

    this.cameraRig.update(this.gameCamera.current().centerWorld, dtSec);

    this.skatersView?.update(snap, homeControlId, nowMs);
    this.goaliesView?.update(snap);
    this.puckView?.update(snap);
    this.fxDirector?.update(dtSec);

    if (this.scoreboard !== null) {
      this.scoreboard.update(snap);
    }
    if (this.faceoffBar !== null) {
      this.faceoffBar.update(snap, this.stage.camera, this.screenScratch);
    }
    // Events only flow while the sim advances; the menu's static snapshot
    // must not re-fire its (already-drained) event list into audio/FX.
    if (this.dispatcher !== null && (this.screen === "Playing" || this.playbackDriver !== null)) {
      this.dispatcher.dispatch(snap);
    }
    if (this.offscreenArrow !== null) {
      this.offscreenArrow.update(snap, this.stage.camera, this.screenScratch);
    }

    this.stage.render();
  }
}
